/**
 * 手書きの SVG 折れ線（Overview の DAU 14 日・Payments の日次売上 30 日）。チャートライブラリを入れない方針のため自作。
 * 値の表示は format で差し替える（人数はそのまま、売上は通貨表記）。点は { date, value }。
 *
 * 見た目の決め事:
 *   - y 軸は 0 始まり（アクティブ数・売上の増減を誇張しない）、目盛りは「きりの良い数」3 本
 *   - 最新日の点だけ強調し値を添える（ダッシュボードで一番見たいのは「今日」）
 *   - ホバー / フォーカスで日ごとの値。スクリーンリーダー用に同じ値を表でも持つ
 *   - 幅は実際の表示幅に合わせる（2026-09-27 dashboard-review の指摘）。以前は viewBox を 640 に固定して縮小していたため、
 *     スマホ（表示幅 約 330px）では目盛りの文字が約 5px になり読めなかった。SVG の 1 単位 ≒ 1px にして文字の大きさを保ち、
 *     狭いときは日付ラベルを間引く（最大 4 個）。props は変えていない（お金の画面と共用のため）
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatDay, formatNumber, useT } from '../i18n';

export interface ChartPoint {
  date: string;
  value: number;
}

function niceMax(v: number) {
  if (v <= 4) return 4;
  const pow = 10 ** Math.floor(Math.log10(v));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

const fmtDay = formatDay;

const plain = (v: number) => formatNumber(v, { maximumFractionDigits: 1 });

export function LineChart({
  data, label, format = plain, tickFormat = format
}: {
  data: ChartPoint[];
  label: string;
  /** 吹き出し・読み上げ用の値の書式 */
  format?: (v: number) => string;
  /** y 軸目盛りの書式（売上は短い表記 "$1.2K" にしたいので吹き出しと分けられる） */
  tickFormat?: (v: number) => string;
}) {
  const id = useId();
  // locale が変わったら日付・数値の書式を描き直す（useT の購読で再描画される）
  const { t } = useT();
  const [hover, setHover] = useState<number | null>(null);
  const figRef = useRef<HTMLElement>(null);
  // 表示幅（px）。測れない環境（テスト・SSR）では従来の 640
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const el = figRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // 280 未満は描き分けても読めないので下限。上限は無し = いつも SVG の 1 単位 ≒ 1px（目盛りの字は CSS の 12px のまま）。
  // 2026-10-18 design/rauno-dashboard2: 以前は上限 640 で、PC の概要（幅 約 1100px）では 1.7 倍に拡大され、目盛りが 18px 前後の等幅になって
  //   数字の札より目立っていた（rauno-audit.md プロジェクトの概要の行）。高さは広い画面だけ少し足す（横長すぎて山が潰れないように）
  const W = Math.max(280, width);
  const H = W < 420 ? 200 : W < 720 ? 220 : 260;
  const max = niceMax(Math.max(0, ...data.map((d) => d.value)));
  // 目盛りの文字（"$2.5K" / "$1.2万" 等）が左で切れないよう、最長の目盛りに合わせて左余白を広げる
  // （半角 約 7px/字、全角 = 日本語の「万」「千」等は約 2 倍で数える）
  const tickWidth = (s: string) => [...s].reduce((w, ch) => w + (ch.charCodeAt(0) > 0xff ? 13 : 7), 0);
  const pad = { l: Math.max(36, 14 + Math.max(...[0, max / 2, max].map((v) => tickWidth(tickFormat(v))))), r: 20, t: 18, b: 30 };
  // 日付ラベルは最大 7〜8 個（14 日 = 1 日おき、30 日 = 5 日おき）。狭い画面で重ならないように
  const labelEvery = Math.max(1, Math.ceil(data.length / (W < 420 ? 4 : 7)));
  const x = (i: number) => pad.l + (i * (W - pad.l - pad.r)) / Math.max(1, data.length - 1);
  const y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);

  const { line, area } = useMemo(() => {
    const pts = data.map((d, i) => [x(i), y(d.value)] as const);
    const line = pts.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join(' ');
    const area = pts.length ? `${line} L${x(data.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z` : '';
    return { line, area };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, max, W, H]);

  const ticks = [0, max / 2, max];
  const last = data.length - 1;
  const active = hover ?? last;
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <figure className="chart" ref={figRef}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={`${id}-cap`} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={`${id}-g`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" className="chart-grad-top" />
            <stop offset="100%" className="chart-grad-bottom" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} className={t === 0 ? 'chart-axis' : 'chart-grid'} />
            <text x={pad.l - 8} y={y(t)} className="chart-tick" textAnchor="end" dominantBaseline="middle">
              {tickFormat(t)}
            </text>
          </g>
        ))}
        {data.map((d, i) =>
          (last - i) % labelEvery === 0 ? (
            <text key={d.date} x={x(i)} y={H - 8} className="chart-tick" textAnchor="middle">
              {fmtDay(d.date)}
            </text>
          ) : null
        )}
        <path d={area} fill={`url(#${id}-g)`} />
        <path d={line} className="chart-line" />
        <line x1={x(active)} x2={x(active)} y1={pad.t} y2={y(0)} className="chart-cursor" />
        {data.map((d, i) => (
          <g key={d.date}>
            {/* 見えない当たり判定（列ごと）。細い線の点を狙わせない */}
            <rect
              x={x(i) - (W - pad.l - pad.r) / Math.max(1, data.length - 1) / 2}
              y={pad.t}
              width={(W - pad.l - pad.r) / Math.max(1, data.length - 1)}
              height={H - pad.t - pad.b}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
            <circle cx={x(i)} cy={y(d.value)} r={i === active ? 5 : 2.5} className={i === active ? 'chart-dot-active' : 'chart-dot'} />
          </g>
        ))}
        {data[active] && (
          // 値の札 = 面の地 + 1px の線の小さな箱（2026-10-18 design/rauno-dashboard2）。以前は字だけで線に重なって読みにくかった。
          // 箱の幅は字数から見積もる（12px の字 ≒ 7px/字 + 左右 8px。tickWidth と同じ数え方）= 描いた後に測り直さない
          (() => {
            const text = `${format(data[active].value)} · ${fmtDay(data[active].date)}`;
            const bw = tickWidth(text) + 16;
            const cx = Math.min(Math.max(x(active), pad.l + bw / 2), W - pad.r - bw / 2);
            return (
              <g transform={`translate(${cx}, ${Math.max(y(data[active].value) - 18, 16)})`}>
                <rect className="chart-callout-box" x={-bw / 2} y={-15} width={bw} height={22} rx={6} />
                <text className="chart-callout" textAnchor="middle">
                  {text}
                </text>
              </g>
            );
          })()
        )}
      </svg>
      <figcaption id={`${id}-cap`} className="sr-only">
        {label}: {data.map((d) => `${fmtDay(d.date)} ${format(d.value)}`).join(', ')}. {t('chart.total', { value: format(total) })}
      </figcaption>
    </figure>
  );
}
